import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { getFiGrowthBook } from '../lib/growthbook';
import { userFreeTrialStatusQuery } from '../graphql-operations/UserIdentity.graphql';
import useCartHasFreeTrialBundle from './useCartHasFreeTrialBundle';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

interface UserFreeTrialStatus {
  currentUser?: {
    userHouseholds?: Array<{
      household?: {
        pets?: Array<{
          device?: {
            hasActiveSubscription?: boolean | null;
            subscription?: {
              hasFreeTrialPlan?: boolean | null;
            } | null;
          } | null;
        }>;
      };
    }>;
  };
}

/**
 * Returns false when the flag is off, a trial is already in cart, the user has an
 * active trial subscription on any device, the user is an existing member
 * (existing members can't start a free trial), or the visitor is enrolled in the
 * hardware-first checkout experiment (the arm and the free trial are mutually exclusive).
 */
export default function useFreeTrialPromoVisible(): boolean {
  const isHardwareFirstEnrolled = useHardwareFirstCheckout();
  const isFlagEnabled = getFiGrowthBook().getFeatureValue<boolean>('free-trial-enabled', false);
  const hasFreeTrialInCart = useCartHasFreeTrialBundle();

  const { data: freeTrialData } = useQuery<UserFreeTrialStatus>(userFreeTrialStatusQuery, {
    skip: isHardwareFirstEnrolled || !isFlagEnabled || hasFreeTrialInCart,
  });

  const hasExistingFreeTrial = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.subscription?.hasFreeTrialPlan === true));
  }, [freeTrialData]);

  const hasActiveMembership = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.hasActiveSubscription === true));
  }, [freeTrialData]);

  return (
    !isHardwareFirstEnrolled && isFlagEnabled && !hasFreeTrialInCart && !hasExistingFreeTrial && !hasActiveMembership
  );
}
